import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Archive,
  ArchiveRestore,
  Ban,
  Inbox,
  Loader2,
  Send,
  WifiOff,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { PlatformLogoBadge } from '@/client/pages/Admin/Media/components/PlatformLogo';
import { MarketingPageShell } from './components/MarketingPageShell';
import {
  MarketingFilterPanel,
  MarketingFilterSelect,
  marketingFilterSelectClass,
  marketingMatchSummary,
} from './components/MarketingListFilters';
import { StatusBadge, EmptyState } from './components/marketing-ui';
import {
  useRepliesQuery,
  useReplyMutation,
  useSetReplyStatusMutation,
} from '@/client/api/marketing/radar';
import type { ApiEngagementReply } from '@/client/types/marketing/api';
import { getApiErrorMessage } from '@/client/utils/errors';
import {
  ENGAGEMENT_STATUS_BADGE,
  ENGAGEMENT_STATUS_LABEL,
  PLATFORM_LABEL,
  type EngagementStatus,
  type SocialPlatform,
} from '@/client/types/marketing';

/**
 * Radar → Inbox → Owned replies, on real data.
 *
 * Two things this page has to get right, because both are easy to render
 * misleadingly:
 *
 * 1. A reply can come back at HTTP 200 with `supported: false` — the provider
 *    genuinely cannot reply on that platform. That is not a failure to retry
 *    into working, so the composer is disabled with the reason shown rather
 *    than a red toast appearing every time someone tries.
 *
 * 2. `sentiment: 'unknown'` means the classifier has not run, not that the
 *    comment is neutral. It gets its own label and is excluded from the
 *    negative-sentiment count — folding it into neutral would turn the
 *    sentiment mix into a chart of how much classification is outstanding.
 */

const STATUS_FILTERS: (EngagementStatus | 'all')[] = ['all', 'unread', 'replied', 'archived'];

const SAVED_REPLIES = [
  'Thanks so much for reaching out — we’ll take a look and follow up shortly!',
  'Really appreciate you flagging this. Could you DM us a booking reference so we can dig in?',
  'Glad you’re loving it! 🎉',
];

const inputClass =
  'h-9 rounded-md border border-gray-200 bg-white px-3 text-sm outline-none ring-blue-500 focus:ring-2';

const SENTIMENT_BADGE: Record<string, string> = {
  positive: 'bg-emerald-50 text-emerald-700',
  neutral: 'bg-gray-100 text-gray-700',
  negative: 'bg-red-50 text-red-700',
  unknown: 'bg-amber-50 text-amber-800',
};

const SENTIMENT_LABEL: Record<string, string> = {
  positive: 'positive',
  neutral: 'neutral',
  negative: 'negative',
  unknown: 'not classified',
};

const EngagementPage = ({ embedded = false }: { embedded?: boolean }) => {
  const [platformFilter, setPlatformFilter] = useState<SocialPlatform | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<EngagementStatus | 'all'>('unread');
  const [replyDrafts, setReplyDrafts] = useState<Record<string, string>>({});
  const [sending, setSending] = useState<string | null>(null);
  /** Threads whose provider cannot reply here. Keyed by engagement id. */
  const [unsupported, setUnsupported] = useState<Record<string, string>>({});

  const { data, isLoading, isError, error } = useRepliesQuery({
    limit: 100,
    status: statusFilter === 'all' ? undefined : statusFilter,
  });
  const sendReply = useReplyMutation();
  const setStatus = useSetReplyStatusMutation();

  const comments: ApiEngagementReply[] = data?.items ?? [];

  const platforms = useMemo(
    () => [...new Set(comments.map((c) => c.platform).filter(Boolean))] as SocialPlatform[],
    [comments],
  );

  const platformFilterOptions = useMemo(
    () => [
      { value: 'all', label: 'All platforms' },
      ...platforms.map((p) => ({ value: p, label: PLATFORM_LABEL[p] ?? p })),
    ],
    [platforms],
  );

  const filtersActive = platformFilter !== 'all' || statusFilter !== 'unread';

  const clearFilters = () => {
    setPlatformFilter('all');
    setStatusFilter('unread');
  };

  const filtered = useMemo(
    () =>
      comments
        .filter((c) => platformFilter === 'all' || c.platform === platformFilter)
        .sort(
          (a, b) =>
            new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime(),
        ),
    [comments, platformFilter],
  );

  const stats = useMemo(
    () => [
      { key: 'total', label: 'Replies', value: isLoading ? 0 : comments.length },
      {
        key: 'unread',
        label: 'Unread',
        value: isLoading ? 0 : comments.filter((c) => c.status === 'unread').length,
      },
      {
        key: 'replied',
        label: 'Replied',
        value: isLoading ? 0 : comments.filter((c) => c.status === 'replied').length,
      },
      {
        key: 'negative',
        // Only genuinely negative. 'unknown' is unclassified, not neutral, and
        // must not be counted either way.
        label: 'Negative sentiment',
        value: isLoading ? 0 : comments.filter((c) => c.sentiment === 'negative').length,
      },
    ],
    [comments, isLoading],
  );

  const submitReply = async (comment: ApiEngagementReply) => {
    const text = (replyDrafts[comment.id] ?? '').trim();
    if (!text) {
      toast.error('Write a reply first');
      return;
    }
    setSending(comment.id);
    try {
      const res = await sendReply.mutateAsync({ engagement_id: comment.id, text });
      if (res?.data?.supported === false) {
        const reason =
          res.data.reason ??
          'This publishing provider cannot post replies on this platform.';
        setUnsupported((prev) => ({ ...prev, [comment.id]: reason }));
        // Not an error toast: nothing went wrong, the capability does not exist.
        toast.message('Replying is not available here', { description: reason });
        return;
      }
      setReplyDrafts((prev) => ({ ...prev, [comment.id]: '' }));
      toast.success('Reply sent');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not send the reply'));
    } finally {
      setSending(null);
    }
  };

  const toggleArchive = (comment: ApiEngagementReply) => {
    const next = comment.status === 'archived' ? 'unread' : 'archived';
    setStatus.mutate(
      { engagement_id: comment.id, status: next },
      {
        onSuccess: () => toast.success(next === 'archived' ? 'Archived' : 'Restored'),
        onError: (err) => toast.error(getApiErrorMessage(err, 'Could not update the reply')),
      },
    );
  };

  const body = (
    <>
      {!embedded && (
        <p className="mb-4 text-sm text-gray-600">
          Coverage triage and brand keyword mentions live under Radar:{' '}
          <Link
            to="/admin/marketing/radar/inbox"
            className="font-medium text-blue-700 hover:underline"
          >
            Inbox
          </Link>
          {' · '}
          <Link
            to="/admin/marketing/radar/inbox?tab=mentions"
            className="font-medium text-blue-700 hover:underline"
          >
            Mentions
          </Link>
          .
        </p>
      )}

      <MarketingFilterPanel
        summary={marketingMatchSummary(filtered.length, comments.length, 'reply', isLoading)}
        showClear={filtersActive}
        onClear={clearFilters}
      >
        <MarketingFilterSelect
          options={platformFilterOptions}
          value={platformFilter}
          onChange={(value) => setPlatformFilter(value as SocialPlatform | 'all')}
          placeholder="All platforms"
          clearValue="all"
          className={marketingFilterSelectClass}
        />
        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm font-medium capitalize transition-colors',
                statusFilter === s ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50',
              )}
            >
              {s === 'all' ? 'All' : ENGAGEMENT_STATUS_LABEL[s]}
            </button>
          ))}
        </div>
      </MarketingFilterPanel>

      {isLoading ? (
        <div className="rounded-xl border border-gray-100 bg-white px-6 py-16 text-center text-sm text-gray-500 shadow-sm">
          <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
          Loading replies…
        </div>
      ) : isError ? (
        <div className="rounded-xl border border-amber-100 bg-amber-50/40 px-6 py-16 text-center text-sm shadow-sm">
          <WifiOff className="mb-2 inline h-5 w-5 text-amber-600" />
          <div className="font-medium text-amber-800">Could not load replies</div>
          <div className="mt-1 text-xs text-gray-500">
            {getApiErrorMessage(error, 'The marketing API did not respond')}
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          label={
            statusFilter === 'unread'
              ? 'Nothing unread — inbox zero.'
              : 'No replies here. Replies appear once posts publish and people respond to them.'
          }
        />
      ) : (
        <div className="space-y-3">
          {filtered.map((comment) => {
            const blocked = unsupported[comment.id];
            const sentiment = comment.sentiment ?? 'unknown';
            return (
              <div
                key={comment.id}
                className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm"
              >
                <div className="flex items-start gap-3">
                  {comment.platform && (
                    <PlatformLogoBadge platform={comment.platform as SocialPlatform} size="sm" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">
                        {comment.author_name ?? comment.author_handle ?? 'Unknown'}
                      </span>
                      {comment.author_handle && (
                        <span className="text-xs text-gray-400">{comment.author_handle}</span>
                      )}
                      {comment.created_at && (
                        <span className="text-xs text-gray-400">
                          · {new Date(comment.created_at).toLocaleString()}
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-gray-700">{comment.body}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      <StatusBadge
                        label={ENGAGEMENT_STATUS_LABEL[comment.status] ?? comment.status}
                        className={ENGAGEMENT_STATUS_BADGE[comment.status] ?? ''}
                      />
                      <StatusBadge
                        label={SENTIMENT_LABEL[sentiment] ?? sentiment}
                        className={SENTIMENT_BADGE[sentiment] ?? SENTIMENT_BADGE.unknown}
                      />
                    </div>

                    {comment.reply ? (
                      <div className="mt-3 rounded-lg bg-gray-50 p-3">
                        <p className="text-xs font-medium text-gray-500">
                          Replied
                          {comment.replied_at &&
                            ` · ${new Date(comment.replied_at).toLocaleString()}`}
                        </p>
                        <p className="mt-1 text-sm text-gray-700">{comment.reply}</p>
                      </div>
                    ) : blocked ? (
                      /* Capability, not failure. Disable and explain rather than
                         offering a Reply button that can never work. */
                      <div className="mt-3 flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                        <Ban className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" />
                        <div>
                          <p className="text-xs font-medium text-gray-700">
                            Replying isn’t available for this account
                          </p>
                          <p className="mt-0.5 text-xs text-gray-500">{blocked}</p>
                        </div>
                      </div>
                    ) : comment.status !== 'archived' ? (
                      <div className="mt-3 space-y-2">
                        <div className="flex flex-wrap gap-1.5">
                          {SAVED_REPLIES.map((sr, i) => (
                            <button
                              key={i}
                              type="button"
                              onClick={() =>
                                setReplyDrafts((prev) => ({ ...prev, [comment.id]: sr }))
                              }
                              className="rounded-full border border-gray-200 px-2 py-0.5 text-[10px] font-medium text-gray-500 hover:bg-gray-50"
                            >
                              {sr.slice(0, 28)}…
                            </button>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <input
                            value={replyDrafts[comment.id] ?? ''}
                            onChange={(e) =>
                              setReplyDrafts((prev) => ({ ...prev, [comment.id]: e.target.value }))
                            }
                            placeholder="Write a reply…"
                            className={`${inputClass} flex-1`}
                          />
                          <Button
                            type="button"
                            size="sm"
                            className="gap-1.5"
                            disabled={sending === comment.id}
                            onClick={() => submitReply(comment)}
                          >
                            <Send className="h-3.5 w-3.5" />
                            {sending === comment.id ? 'Sending…' : 'Reply'}
                          </Button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1.5">
                    <button
                      type="button"
                      onClick={() => toggleArchive(comment)}
                      className="flex items-center gap-1 text-xs font-medium text-gray-400 hover:text-gray-700"
                      title={comment.status === 'archived' ? 'Restore' : 'Archive'}
                    >
                      {comment.status === 'archived' ? (
                        <ArchiveRestore className="h-3.5 w-3.5" />
                      ) : (
                        <Archive className="h-3.5 w-3.5" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  if (embedded) return body;

  return (
    <MarketingPageShell
      title="Engagement"
      description="Comments and replies on your owned social posts. Archive a thread once it's handled — replying is not the only way to clear one."
      crumbs={[
        { label: 'Marketing', href: '/admin/marketing' },
        { label: 'Radar', href: '/admin/marketing/radar' },
        { label: 'Inbox', href: '/admin/marketing/radar/inbox' },
        { label: 'Replies' },
      ]}
      icon={Inbox}
      stats={stats}
    >
      {body}
    </MarketingPageShell>
  );
};

export default EngagementPage;
